
/**
 * manager-cart-drawer.js
 * Gerencia o drawer lateral do carrinho.
 */

class CartDrawerManager {
  constructor() {
    this._sectionId = window.cartDrawerConfig?.sectionId;
    this._isRefreshing = false;
    this._shippingStorageKey = 'cartDrawerShippingEstimate';
    this._toolsAlwaysVisible = window.cartDrawerConfig?.toolsMode === 'always_open';
    this._toolsOpen = true;

    this._onCartAdded = this._onCartAdded.bind(this);
    this._onCartUpdated = this._onCartUpdated.bind(this);
    this._onCartRemoved = this._onCartRemoved.bind(this);
    this._onContentClick = this._onContentClick.bind(this);
    this._onFormSubmit = this._onFormSubmit.bind(this);

    this._bindEvents();
    this._restoreShippingState();
    this._syncToolsPanel();
  }

  _bindEvents() {
    document.addEventListener('cart:added', this._onCartAdded);
    document.addEventListener('cart:updated', this._onCartUpdated);
    document.addEventListener('cart:removed', this._onCartRemoved);
    document.addEventListener('click', this._onContentClick);
    document.addEventListener('submit', this._onFormSubmit);
  }

  async _onCartAdded(e) {
    await this.refresh();
    if (!e.detail?.action || e.detail.action === 'open_drawer') {
      window.DrawerManager?.open('cart');
    }
  }

  async _onCartUpdated() {
    await this.refresh();
  }

  async _onCartRemoved() {
    await this.refresh();
  }

  _onContentClick(e) {
    if (!e.target.closest('[data-drawer="cart"]')) return;

    const toggleBtn = e.target.closest('[data-cart-tools-toggle]');
    if (toggleBtn) {
      this._toolsOpen = !this._toolsOpen;
      this._syncToolsPanel();
      return;
    }

    const removeDiscountBtn = e.target.closest('[data-cart-discount-remove]');
    if (removeDiscountBtn) {
      this._removeDiscount(removeDiscountBtn);
      return;
    }

    const removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      const key = removeBtn.dataset.lineItemKey;
      if (key) window.CartManager?.remove(key, { button: removeBtn });
      return;
    }

    const minusBtn = e.target.closest('[data-quantity-minus]');
    if (minusBtn) {
      const key = minusBtn.dataset.lineItemKey;
      const qty = this._getQuantity(minusBtn);
      if (!key) return;
      if (qty <= 1) {
        window.CartManager?.remove(key, { button: minusBtn });
      } else {
        window.CartManager?.update(key, qty - 1, { button: minusBtn });
      }
      return;
    }

    const plusBtn = e.target.closest('[data-quantity-plus]');
    if (plusBtn) {
      const key = plusBtn.dataset.lineItemKey;
      const qty = this._getQuantity(plusBtn);
      if (key) window.CartManager?.update(key, qty + 1, { button: plusBtn });
    }
  }

  async _onFormSubmit(e) {
    if (!e.target.closest('[data-drawer="cart"]')) return;

    const shippingForm = e.target.closest('[data-cart-shipping-form]');
    if (shippingForm) {
      e.preventDefault();
      await this._handleShippingSubmit(shippingForm);
      return;
    }

    const discountForm = e.target.closest('[data-cart-discount-form]');
    if (discountForm) {
      e.preventDefault();
      await this._handleDiscountSubmit(discountForm);
    }
  }

  _getQuantity(btn) {
    const item = btn.closest('[data-cart-item]');
    const valueEl = item?.querySelector('span[data-quantity-value]');
    return parseInt(valueEl?.textContent?.trim() || '1', 10);
  }

  async _handleShippingSubmit(form) {
    const zipInput = form.querySelector('[data-cart-shipping-zip]');
    const button = form.querySelector('[data-cart-shipping-submit]');
    const zip = zipInput?.value?.trim();
    const normalizedZip = zip?.replace(/\D/g, '') || '';
    const country = form.dataset.shippingCountry || 'Brazil';
    const province = form.dataset.shippingProvince || '';

    if (!zip) {
      this._renderShippingResult({
        status: 'error',
        message: form.dataset.emptyMessage || 'Informe um CEP.'
      });
      return;
    }

    if (normalizedZip === '00000000') {
      sessionStorage.removeItem(this._shippingStorageKey);
      this._renderShippingResult({
        status: 'error',
        message: 'CEP inválido'
      });
      if (button) window.ButtonFeedback?.error(button);
      return;
    }

    if (form.dataset.freeShippingUnlocked === 'true') {
      this._renderShippingResult({
        status: 'success',
        rate: { price: '0' }
      });
      if (button) window.ButtonFeedback?.success(button);
      return;
    }

    if (button) window.ButtonFeedback?.loading(button);

    let finalProvince = province;
    if (country === 'Brazil' && !finalProvince) {
      try {
        const viacepRes = await fetch(
          `https://viacep.com.br/ws/${normalizedZip}/json/`
        );
        const viacepData = await viacepRes.json();
        if (viacepData.uf) {
          finalProvince = viacepData.uf;
        } else {
          throw new Error('CEP não encontrado ou inválido.');
        }
      } catch (error) {
        this._renderShippingResult({
          status: 'error',
          message: error.message || 'Erro ao consultar o CEP.'
        });
        if (button) window.ButtonFeedback?.error(button);
        return;
      }
    }

    try {
      const result = await this._fetchShippingRate(
        zip,
        country,
        finalProvince
      );
      sessionStorage.setItem(
        this._shippingStorageKey,
        JSON.stringify(result)
      );
      this._restoreShippingState();
      if (button) window.ButtonFeedback?.success(button);
    } catch (error) {
      this._renderShippingResult({
        status: 'error',
        message: error.message
      });
      if (button) window.ButtonFeedback?.error(button);
    }
  }

  calculateShipping(form) {
    return this._handleShippingSubmit(form);
  }

  restoreShippingState() {
    this._restoreShippingState();
  }

  async _handleDiscountSubmit(form) {
    const input = form.querySelector('[data-cart-discount-input]');
    const button = form.querySelector('[data-cart-discount-submit]');
    const code = input?.value?.trim();

    if (!code) return;

    if (!input.dataset.originalPlaceholder) {
      input.dataset.originalPlaceholder =
        input.getAttribute('placeholder') || 'CUPOM DE DESCONTO';
    }

    const result = await window.CartManager?.applyDiscount(code, { button });

    if (!result?.success) {
      input.value = '';
      input.placeholder = 'Código Inválido';
      input.classList.add('cart-discount-error');

      input.addEventListener('focus', function resetInput() {
        input.placeholder = input.dataset.originalPlaceholder;
        input.classList.remove('cart-discount-error');
        input.removeEventListener('focus', resetInput);
      }, { once: true });
    }
  }

  async _removeDiscount(button) {
    await window.CartManager?.removeDiscount({ button });
  }

  async _fetchShippingRate(zip, country, province) {
    const query = new URLSearchParams();
    query.set('shipping_address[zip]', zip);
    query.set('shipping_address[country]', country);
    if (province) query.set('shipping_address[province]', province);

    const root = window.Shopify?.routes?.root || '/';
    const prepareUrl = `${root}cart/prepare_shipping_rates.json?${query.toString()}`;
    const asyncUrl = `${root}cart/async_shipping_rates.json?${query.toString()}`;

    await fetch(prepareUrl, { method: 'POST' });

    let payload = null;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      await this._wait(700);
      const response = await fetch(asyncUrl, { cache: 'no-store' });
      payload = await response.json();
      if (payload?.shipping_rates?.length) break;
    }

    if (!payload?.shipping_rates?.length) {
      throw new Error(
        document.querySelector('[data-cart-shipping-result]')?.dataset.errorMessage ||
        'CEP inválido'
      );
    }

    const sortedRates = [...payload.shipping_rates].sort(
      (a, b) => parseFloat(a.price) - parseFloat(b.price)
    );
    const cheapestRate = sortedRates[0];

    return {
      zip,
      country,
      province,
      status: 'success',
      rate: cheapestRate,
    };
  }

  _wait(ms) {
    return new Promise(resolve => window.setTimeout(resolve, ms));
  }

  async refresh() {
    // A atualização visual não deve bloquear uma nova atualização.
    if (!this._sectionId) return;

    if (this._isRefreshing) {
      this._refreshQueued = true;
      return;
    }

    this._isRefreshing = true;
    this._setLoading(true);

    try {
      do {
        this._refreshQueued = false;

        // Evita reaproveitar HTML antigo das seções da Shopify.
        const root = window.Shopify?.routes?.root || '/';
        const params = new URLSearchParams({
          sections: this._sectionId,
          cartRefresh: String(Date.now())
        });

        const res = await fetch(`${root}?${params.toString()}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const sections = await res.json();
        const html = sections[this._sectionId];

        if (!html) {
          throw new Error('Seção da sacola não retornou HTML.');
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newContent = doc.querySelector('[data-cart-drawer-content]');
        const curContent = document.querySelector('[data-cart-drawer-content]');
        const newSummary = doc.querySelector('[data-cart-drawer-summary]');
        const curSummary = document.querySelector('[data-cart-drawer-summary]');

        if (newContent && curContent) {
          curContent.className = newContent.className;
          curContent.innerHTML = newContent.innerHTML;
        }

        if (newSummary && curSummary) {
          curSummary.outerHTML = newSummary.outerHTML;
        }

        this._updateCount(doc);
        this._restoreShippingState();
        this._syncToolsPanel(true);

      } while (this._refreshQueued);

    } catch (err) {
      console.error('[CartDrawerManager] Refresh failed:', err);
    } finally {
      this._isRefreshing = false;
      this._setLoading(false);
    }
  }

  _setLoading(show) {
    const el = document.querySelector('[data-cart-drawer-loading]');
    if (!el) return;
    show ? el.removeAttribute('hidden') : el.setAttribute('hidden', '');
  }

  _updateCount(doc) {
    const countEl = doc.querySelector('[data-cart-count]');
    if (!countEl) return;

    const count = countEl.textContent.trim();

    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
    });

    document.querySelectorAll('[data-cart-count-zero]').forEach(el => {
      el.hidden = count === '0';
    });
  }

  _restoreShippingState() {
    const rawState = sessionStorage.getItem(this._shippingStorageKey);

    if (!rawState) {
      const freeShippingTarget = document.querySelector(
        '[data-cart-shipping-result][data-free-shipping-unlocked="true"]'
      );

      if (freeShippingTarget) {
        this._renderShippingResult({
          status: 'success',
          rate: { price: '0' }
        });
      }
      return;
    }

    try {
      const state = JSON.parse(rawState);

      document.querySelectorAll('[data-cart-shipping-zip]').forEach(input => {
        if (state.zip) input.value = state.zip;
      });

      this._renderShippingResult(state);
    } catch (error) {
      sessionStorage.removeItem(this._shippingStorageKey);
    }
  }

  _renderShippingResult(state) {
    this._renderCartTotal(state);

    const targets = document.querySelectorAll('[data-cart-shipping-result]');
    const rows = document.querySelectorAll('[data-cart-shipping-row]');

    if (!targets.length) return;

    if (!state) {
      rows.forEach(row => row.hidden = true);
      targets.forEach(target => {
        target.textContent = '';
        delete target.dataset.status;
      });
      return;
    }

    rows.forEach(row => row.hidden = false);

    targets.forEach(target => {
      target.dataset.status = state.status || 'success';

      if (state.status === 'error') {
        target.textContent =
          state.message || target.dataset.errorMessage || 'CEP inválido';
        return;
      }

      if (target.dataset.freeShippingUnlocked === 'true') {
        target.textContent = target.dataset.freeShippingLabel || 'Gratis';
        return;
      }

      const price = this._formatRatePrice(
        state.rate?.price,
        target.dataset.currencyCode || 'BRL'
      );
      target.textContent = price;
    });
  }

  _renderCartTotal(state) {
    const targets = document.querySelectorAll('[data-cart-total-value]');

    if (!targets.length) return;

    targets.forEach(target => {
      const baseCents = parseInt(target.dataset.baseTotal || '0', 10);
      let total = baseCents / 100;
      const freeShippingUnlocked =
        target.dataset.freeShippingUnlocked === 'true';

      if (state?.status === 'success' && !freeShippingUnlocked) {
        const shipping = parseFloat(state.rate?.price || '0');
        if (Number.isFinite(shipping)) total += shipping;
      }

      target.textContent = this._formatRatePrice(
        total,
        target.dataset.currencyCode || 'BRL'
      );
    });
  }

  _formatRatePrice(price, currencyCode) {
    const value = parseFloat(price || '0');
    const locale = document.documentElement.lang || 'pt-BR';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode || 'BRL',
      }).format(value);
    } catch (error) {
      return value.toFixed(2);
    }
  }

  _syncToolsPanel(skipAnimation = false) {
    const panel = document.querySelector('[data-cart-tools-panel]');
    if (!panel) return;

    const toggle = document.querySelector('[data-cart-tools-toggle]');
    const alwaysVisible =
      panel.dataset.toolsMode === 'always_open' || this._toolsAlwaysVisible;

    if (alwaysVisible) {
      this._toolsOpen = true;
      panel.hidden = false;
      panel.classList.add('is-open');
      panel.style.maxHeight = 'none';

      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      return;
    }

    if (!toggle) return;

    toggle.setAttribute(
      'aria-expanded',
      this._toolsOpen ? 'true' : 'false'
    );

    if (this._toolsOpen) {
      panel.hidden = false;
      const targetHeight = `${panel.scrollHeight}px`;

      if (skipAnimation) {
        panel.classList.add('is-open');
        panel.style.maxHeight = 'none';
        return;
      }

      panel.style.maxHeight = '0px';

      requestAnimationFrame(() => {
        panel.classList.add('is-open');
        panel.style.maxHeight = targetHeight;
      });

      window.setTimeout(() => {
        if (this._toolsOpen) panel.style.maxHeight = 'none';
      }, 260);

      return;
    }

    if (skipAnimation) {
      panel.classList.remove('is-open');
      panel.style.maxHeight = '0px';
      panel.hidden = true;
      return;
    }

    const currentHeight = panel.scrollHeight;
    panel.style.maxHeight = `${currentHeight}px`;

    requestAnimationFrame(() => {
      panel.classList.remove('is-open');
      panel.style.maxHeight = '0px';
    });

    window.setTimeout(() => {
      if (!this._toolsOpen) panel.hidden = true;
    }, 260);
  }

  destroy() {
    document.removeEventListener('cart:added', this._onCartAdded);
    document.removeEventListener('cart:updated', this._onCartUpdated);
    document.removeEventListener('cart:removed', this._onCartRemoved);
    document.removeEventListener('click', this._onContentClick);
    document.removeEventListener('submit', this._onFormSubmit);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.CartDrawerManager = new CartDrawerManager();
});
